import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Hand, Link2, Pencil, Trash2, RotateCcw, X, MessageSquare, Undo2, Send, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/contexts";
import { fmtDate, isViewer } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import ContactForm from "./contacts/ContactForm";
import { toast } from "sonner";

const openWord = (n) => `open ${n} day${n === 1 ? "" : "s"}`;

function LeadForm({ open, onClose, initial, onSaved }) {
  const [f, setF] = useState({ company_name: "", product_name: "", remarks: "" });
  const [links, setLinks] = useState([""]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF({
      company_name: initial?.company_name || "",
      product_name: initial?.product_name || "",
      remarks: initial?.remarks || "",
    });
    setLinks(initial?.website_links?.length ? [...initial.website_links] : [""]);
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = links.map((l) => l.trim()).filter(Boolean);
    if (!f.company_name.trim() || clean.length === 0) {
      toast.error("Company Name and at least one Website Link are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...f, website_links: clean };
      if (initial) await api.put(`/leads/${initial.id}`, payload);
      else await api.post("/leads", payload);
      toast.success(initial ? "Lead updated" : "Lead published to the board");
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Edit Lead" : "New Grab A Deal"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Company Name <span className="text-destructive">*</span></Label>
            <Input data-testid="lead-company" value={f.company_name}
              onChange={(e) => setF({ ...f, company_name: e.target.value })} placeholder="e.g. Nourish Bars India Pvt Ltd" />
          </div>
          <div className="space-y-2">
            <Label>Website Links <span className="text-destructive">*</span></Label>
            {links.map((l, i) => (
              <div key={`link-${i}`} className="flex gap-2">
                <Input data-testid={`lead-link-${i}`} value={l}
                  onChange={(e) => setLinks(links.map((x, j) => (j === i ? e.target.value : x)))}
                  placeholder="www.example.com" />
                {links.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" data-testid={`remove-link-${i}`}
                    onClick={() => setLinks(links.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="add-link"
              onClick={() => setLinks([...links, ""])}><Plus className="w-3 h-3 mr-1" />Add another link</Button>
          </div>
          <div>
            <Label>Product Name</Label>
            <Input data-testid="lead-product" value={f.product_name}
              onChange={(e) => setF({ ...f, product_name: e.target.value })} placeholder="Optional — leave blank if unknown" />
          </div>
          <div>
            <Label>Remarks</Label>
            <Textarea data-testid="lead-remarks" rows={4} value={f.remarks}
              onChange={(e) => setF({ ...f, remarks: e.target.value })}
              placeholder="e.g. Maybe they are using Pea Protein Isolate — their protein bar range launched last month" />
          </div>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="lead-save" disabled={saving}
              className="bg-primary hover:bg-primary/90 text-white disabled:opacity-60">
              {saving ? "Saving…" : initial ? "Save changes" : "Publish to board"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const CommentBadge = ({ lead, className = "" }) =>
  lead.comment_count > 0 ? (
    <span data-testid={`comment-badge-${lead.id}`}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full
        bg-amber-100 text-amber-800 border border-amber-300 ${className}`}>
      <MessageSquare className="w-3 h-3" />
      {lead.comment_count} {lead.comment_count === 1 ? "comment" : "comments"}
    </span>
  ) : null;

export const LeadCard = ({ lead, onOpen, onGrab, compact }) => {
  const { user } = useAuth();
  const viewer = isViewer(user);
  const isOpen = lead.status === "open";
  const isDeleted = lead.status === "deleted";
  return (
    <div data-testid={`lead-card-${lead.id}`} onClick={() => onOpen?.(lead)}
      className={`relative border rounded-md ${compact ? "p-2" : "p-3"} cursor-pointer transition-colors overflow-hidden
        ${isDeleted ? "bg-destructive/5 border-destructive/30 hover:border-destructive/60"
          : isOpen ? "bg-white border-border hover:border-primary/50"
          : "bg-secondary/40 border-border/60 opacity-80 hover:opacity-100"}`}>
      {isOpen && <span className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />}
      {isDeleted && <span className="absolute left-0 top-0 bottom-0 w-1 bg-destructive/70" />}
      <div className="pl-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-semibold truncate ${isDeleted ? "line-through text-muted-foreground" : ""}`}>
                {lead.company_name}
              </span>
              <CommentBadge lead={lead} />
            </div>
            <div className="text-[11px] text-muted-foreground">
              {lead.product_name || "Product not specified"} · published {fmtDate(lead.created_at)}
            </div>
          </div>
          {isDeleted ? (
            <span className="text-[11px] text-destructive shrink-0 font-medium">
              Deleted by {lead.deleted_by_name} · {fmtDate(lead.deleted_at)}
            </span>
          ) : isOpen && !viewer ? (
            <Button size="sm" data-testid={`grab-${lead.id}`}
              onClick={(e) => { e.stopPropagation(); onGrab(lead); }}
              className={`bg-amber-600 hover:bg-amber-700 text-white shrink-0 ${compact ? "h-7 px-2 text-xs" : ""}`}>
              <Hand className="w-3.5 h-3.5 mr-1" />Grab It
            </Button>
          ) : isOpen ? (
            <span className="text-[11px] text-amber-700 shrink-0 font-medium">Open for grabs</span>
          ) : (
            <span className="text-[11px] text-muted-foreground shrink-0">
              Grabbed by {lead.grabbed_by_name} · {fmtDate(lead.grabbed_at)}
            </span>
          )}
        </div>
        {!compact && (
          <>
            {lead.website_links?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {lead.website_links.map((l) => (
                  <a key={l} href={l} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-[10px] bg-neutral-100 hover:bg-neutral-200 px-1.5 py-0.5 rounded">
                    <Link2 className="w-3 h-3" />{l.replace(/^https?:\/\//, "").slice(0, 38)}
                  </a>
                ))}
              </div>
            )}
            {lead.remarks && (
              <p className="mt-2 text-sm border-l-2 border-primary/30 pl-3 italic text-neutral-700">{lead.remarks}</p>
            )}
            {isDeleted && lead.deleted_reason && (
              <p data-testid={`deleted-reason-${lead.id}`}
                className="mt-2 text-sm border-l-2 border-destructive/40 pl-3 text-destructive">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{lead.deleted_reason}
              </p>
            )}
            <div className="mt-2 text-[11px] text-muted-foreground">
              {isDeleted ? "In Deleted Deals" : isOpen ? openWord(lead.days_open ?? 0) : `grabbed after ${lead.days_to_grab ?? 0} day${lead.days_to_grab === 1 ? "" : "s"}`}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function CommentRow({ c, replies, canDelete, onReply, onDelete }) {
  const isDeletion = c.kind === "deletion";
  return (
    <div data-testid={`comment-${c.id}`}
      className={`rounded-md border p-2.5 ${isDeletion ? "border-destructive/30 bg-destructive/5" : "border-border bg-white"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground">{c.created_by_name}</span>
          {c.created_by_role === "manager" && <span className="ml-1 text-primary">· Manager</span>}
          <span className="ml-1">· {fmtDate(c.created_at)}</span>
          {isDeletion && <span className="ml-1 text-destructive font-medium">· deletion reason</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" data-testid={`reply-${c.id}`} onClick={() => onReply(c.id)}
            className="text-[11px] text-primary hover:underline">Reply</button>
          {canDelete(c) && (
            <button type="button" data-testid={`delete-comment-${c.id}`} onClick={() => onDelete(c)}
              className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
          )}
        </div>
      </div>
      <p className="mt-1 text-sm whitespace-pre-wrap">{c.body}</p>
      {replies.length > 0 && (
        <div className="mt-2 space-y-1.5 border-l-2 border-primary/25 pl-2.5">
          {replies.map((r) => (
            <div key={r.id} data-testid={`comment-${r.id}`} className="text-sm">
              <div className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                <span>
                  <span className="font-semibold text-foreground">{r.created_by_name}</span>
                  {r.created_by_role === "manager" && <span className="ml-1 text-primary">· Manager</span>}
                  <span className="ml-1">· {fmtDate(r.created_at)}</span>
                </span>
                {canDelete(r) && (
                  <button type="button" data-testid={`delete-comment-${r.id}`} onClick={() => onDelete(r)}
                    className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
                )}
              </div>
              <p className="whitespace-pre-wrap">{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadComments({ leadId, user, isManager, onChanged }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/leads/${leadId}/comments`);
      setRows(r.data);
      setLoadError("");
    } catch (err) {
      // Keep the dialog usable, but say so instead of failing silently.
      console.error("Could not load lead comments", err);
      const detail = err.response?.data?.detail || "Could not load the comments on this lead";
      setLoadError(detail);
      toast.error(detail);
    }
  }, [leadId]);
  useEffect(() => { if (leadId) load(); }, [leadId, load]);

  const post = async (body, parent_id) => {
    if (!body.trim()) { toast.error("Please write something first"); return; }
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/comments`, { body, parent_id: parent_id || null });
      setText(""); setReplyText(""); setReplyTo(null);
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not post your comment"); }
    setBusy(false);
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await api.delete(`/leads/comments/${c.id}`); await load(); onChanged?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const canDelete = (c) => isManager || c.created_by === user?.id;
  const ids = new Set(rows.map((r) => r.id));
  // A reply whose parent was removed still deserves to be read.
  const roots = rows.filter((r) => !r.parent_id || !ids.has(r.parent_id));

  return (
    <div className="space-y-2" data-testid="lead-comments">
      <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5" />Comments
        <span data-testid="comment-total" className="bg-secondary px-1.5 rounded-full text-[10px] normal-case tracking-normal">{rows.length}</span>
      </div>
      {loadError ? (
        <div className="text-xs text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-2 flex items-center justify-between gap-2"
          data-testid="comments-load-error">
          <span>Could not load the comments on this lead.</span>
          <button type="button" onClick={load} className="font-semibold underline">Try again</button>
        </div>
      ) : roots.length === 0 && (
        <div className="text-xs italic text-muted-foreground">
          No comments yet — add what you know about this company.
        </div>
      )}
      <div className="space-y-2">
        {roots.map((c) => (
          <div key={c.id}>
            <CommentRow c={c} replies={rows.filter((r) => r.parent_id === c.id)}
              canDelete={canDelete} onReply={(id) => { setReplyTo(id); setReplyText(""); }} onDelete={remove} />
            {replyTo === c.id && (
              <div className="mt-1.5 flex gap-2 pl-2.5">
                <Input data-testid="reply-input" autoFocus value={replyText}
                  onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply…" className="h-9 text-sm" />
                <Button size="sm" data-testid="reply-send" disabled={busy}
                  onClick={() => post(replyText, c.id)} className="bg-primary hover:bg-primary/90 text-white">
                  <Send className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>Cancel</Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <Textarea data-testid="comment-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="e.g. Already contacted this company but they are not using our ingredients" />
      <div className="flex justify-end">
        <Button size="sm" data-testid="comment-post" disabled={busy} onClick={() => post(text)}
          className="bg-primary hover:bg-primary/90 text-white disabled:opacity-60">
          {busy ? "Posting…" : "Post comment"}
        </Button>
      </div>
    </div>
  );
}

function LeadDetail({ lead, onClose, onGrab, onRefresh, isManager, users, onEdit, user }) {
  const [delReason, setDelReason] = useState("");
  const [asking, setAsking] = useState(false);
  useEffect(() => { setAsking(false); setDelReason(""); }, [lead?.id]);
  if (!lead) return null;
  const isOpen = lead.status === "open";
  const isDeleted = lead.status === "deleted";
  const act = async (path, body) => {
    try { await api.post(`/leads/${lead.id}/${path}`, body || {}); toast.success("Done"); onRefresh(); onClose(); }
    catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };
  return (
    <Dialog open={!!lead} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{lead.company_name}</DialogTitle>
          <DialogDescription className="sr-only">Lead details, comments and actions</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="text-[11px] text-muted-foreground">
            Published by {lead.created_by_name} on {fmtDate(lead.created_at)} · {isOpen ? openWord(lead.days_open ?? 0) : `grabbed after ${lead.days_to_grab ?? 0} day(s)`}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Website links</div>
            <div className="space-y-1 mt-1">
              {lead.website_links?.map((l) => (
                <a key={l} href={l} target="_blank" rel="noreferrer" data-testid={`lead-link-open-${l}`}
                  className="flex items-center gap-1.5 text-primary hover:underline break-all">
                  <Link2 className="w-3.5 h-3.5 shrink-0" />{l}
                </a>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Product</div>
            <div>{lead.product_name || "Product not specified"}</div>
          </div>
          {lead.remarks && (
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Remarks</div>
              <p className="mt-1 text-base border-l-2 border-primary/30 pl-3 italic text-neutral-700">{lead.remarks}</p>
            </div>
          )}
          {isDeleted && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-destructive" data-testid="lead-deleted-banner">
              <div className="font-semibold text-sm flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />Moved to Deleted Deals
              </div>
              <div className="text-xs mt-0.5">
                by {lead.deleted_by_name} on {fmtDate(lead.deleted_at)}
              </div>
              {lead.deleted_reason && <p className="mt-1.5 text-sm">{lead.deleted_reason}</p>}
            </div>
          )}
          {!isOpen && !isDeleted && (
            <div className="rounded-md border border-border bg-secondary/50 p-3" data-testid="lead-grabbed-by">
              Grabbed by <span className="font-semibold">{lead.grabbed_by_name}</span> on {fmtDate(lead.grabbed_at)}
            </div>
          )}

          <div className="border-t border-border pt-3">
            <LeadComments leadId={lead.id} user={user} isManager={isManager} onChanged={onRefresh} />
          </div>

          {asking && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-2" data-testid="delete-reason-box">
              <Label className="text-destructive">Why are you deleting this lead? <span className="text-destructive">*</span></Label>
              <Textarea data-testid="delete-reason" rows={3} value={delReason} onChange={(e) => setDelReason(e.target.value)}
                placeholder="e.g. Already contacted, they do not use our ingredients" />
              <p className="text-[11px] text-muted-foreground">
                It moves to Deleted Deals with this note so you can check it before publishing the company again.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setAsking(false)}>Cancel</Button>
                <Button size="sm" data-testid="confirm-delete-lead" disabled={!delReason.trim()}
                  className="bg-destructive hover:bg-destructive/90 text-white disabled:opacity-50"
                  onClick={async () => {
                    if (!delReason.trim()) { toast.error("Please write a reason"); return; }
                    try {
                      await api.post(`/leads/${lead.id}/delete`, { reason: delReason.trim() });
                      toast.success("Moved to Deleted Deals"); onRefresh(); onClose();
                    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
                  }}>
                  Move to Deleted Deals
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
            {isManager && (
              <>
                {!isDeleted && (
                  <Button variant="outline" size="sm" data-testid="lead-edit" onClick={() => { onEdit(lead); onClose(); }}>
                    <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                  </Button>
                )}
                {!isOpen && !isDeleted && (
                  <>
                    <Button variant="outline" size="sm" data-testid="lead-release" onClick={() => act("release")}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />Release
                    </Button>
                    <select data-testid="lead-reassign" defaultValue=""
                      onChange={(e) => e.target.value && act("reassign", { user_id: e.target.value })}
                      className="h-8 rounded-md border border-input bg-white px-2 text-xs">
                      <option value="">Reassign to…</option>
                      {users.filter((u) => u.id !== lead.grabbed_by).map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </>
                )}
                {isDeleted ? (
                  <>
                    <Button variant="outline" size="sm" data-testid="lead-restore" onClick={() => act("restore")}>
                      <Undo2 className="w-3.5 h-3.5 mr-1" />Restore to board
                    </Button>
                    <Button variant="outline" size="sm" data-testid="lead-purge"
                      className="border-destructive/40 text-destructive hover:bg-destructive/10"
                      onClick={async () => {
                        if (!window.confirm("Remove this lead permanently? Its comments go with it.")) return;
                        try { await api.delete(`/leads/${lead.id}`); toast.success("Lead removed permanently"); onRefresh(); onClose(); }
                        catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
                      }}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Remove permanently
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" data-testid="lead-delete"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10"
                    onClick={() => setAsking(true)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                  </Button>
                )}
              </>
            )}
            {isOpen && !isViewer(user) && (
              <Button size="sm" data-testid="lead-detail-grab" className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => { onGrab(lead); onClose(); }}>
                <Hand className="w-3.5 h-3.5 mr-1" />Grab It
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Shared by the board and the dashboard panel: claims the lead only after the
// contact has been saved, so clicking first never reserves anything.
export async function claimLead(lead, contact, onDone) {
  try {
    await api.post(`/leads/${lead.id}/grab`, { contact_id: contact.id });
    toast.success(`You grabbed ${lead.company_name}`, { description: "The contact is now yours on the Contacts page." });
  } catch (err) {
    toast.error(err.response?.data?.detail || "Could not claim this lead", { duration: 7000 });
  }
  onDone?.();
}

// Dashboard panel: the same two groups at half height, capped at five each.
export function GrabADealPanel() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [grabbing, setGrabbing] = useState(null);
  const [meta, setMeta] = useState({ fields: [], products: [], users: [] });
  const load = useCallback(() => api.get("/leads")
    .then((r) => { setLeads(r.data); window.dispatchEvent(new Event("leads-changed")); })
    .catch((err) => console.error("Could not load the Grab A Deal panel", err)), []);
  useEffect(() => {
    load();
    Promise.all([
      api.get("/fields", { params: { entity: "contact" } }),
      api.get("/products"),
      api.get("/users"),
    ]).then(([f, p, u]) => setMeta({ fields: f.data, products: p.data, users: u.data })).catch(() => {});
  }, [load]);
  // The panel stays short on purpose: three newest open leads and the single
  // latest grab — the rest live on the full Grab A Deal board.
  const open = leads.filter((l) => l.status === "open").slice(0, 3);
  const taken = leads.filter((l) => l.status === "grabbed").slice(0, 1);
  const openCount = leads.filter((l) => l.status === "open").length;
  const takenCount = leads.filter((l) => l.status === "grabbed").length;

  return (
    <div className="border-2 border-amber-300 rounded-lg bg-amber-50/40 p-3 space-y-2 h-full" data-testid="dashboard-grab-panel">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-semibold flex items-center gap-2">
          <Hand className="w-4 h-4 text-amber-600" />Grab A Deal
          <span data-testid="dashboard-open-count"
            className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full">{openCount} open</span>
        </h3>
        <Link to="/grab-a-deal" data-testid="see-all-leads" className="text-xs text-primary hover:underline">See all →</Link>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
          Open for Grabs {openCount > open.length && <span className="font-normal normal-case tracking-normal">· showing the 3 latest</span>}
        </div>
        {open.length === 0 ? <div className="text-xs italic text-muted-foreground">Nothing open right now.</div> : (
          <div className="space-y-1">{open.map((l) => <LeadCard key={l.id} lead={l} compact onGrab={setGrabbing} />)}</div>
        )}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
          Recently Grabbed {takenCount > taken.length && <span className="font-normal normal-case tracking-normal">· showing the latest one</span>}
        </div>
        {taken.length === 0 ? <div className="text-xs italic text-muted-foreground">No leads grabbed yet.</div> : (
          <div className="space-y-1">{taken.map((l) => <LeadCard key={l.id} lead={l} compact />)}</div>
        )}
      </div>
      <ContactForm open={!!grabbing} onClose={() => setGrabbing(null)}
        prefill={grabbing ? {
          company_name: grabbing.company_name,
          website: grabbing.website_links?.[0] || "",
          remarks: grabbing.remarks || "",
        } : null}
        onSave={async (contact) => {
          if (grabbing && contact?.id) await claimLead(grabbing, contact, load);
          setGrabbing(null);
        }}
        fields={meta.fields} products={meta.products} users={meta.users} currentUser={user} />
    </div>
  );
}

export default function GrabADeal() {  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [fields, setFields] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [grabber, setGrabber] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [grabbing, setGrabbing] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (status) params.status = status;
    if (grabber) params.grabbed_by = grabber;
    const r = await api.get("/leads", { params });
    setLeads(r.data);
    window.dispatchEvent(new Event("leads-changed"));
  }, [search, status, grabber]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/fields", { params: { entity: "contact" } }).then((r) => setFields(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const openLeads = useMemo(() => leads.filter((l) => l.status === "open"), [leads]);
  const grabbed = useMemo(() => leads.filter((l) => l.status === "grabbed"), [leads]);
  const deletedLeads = useMemo(() => leads.filter((l) => l.status === "deleted"), [leads]);

  const Group = ({ title, rows, testid, empty }) => (
    <section className="space-y-2" data-testid={testid}>
      <h2 className="text-base font-heading font-semibold flex items-center gap-2">
        {title}
        <span className="text-xs font-normal bg-secondary px-2 py-0.5 rounded-full text-muted-foreground">{rows.length}</span>
      </h2>
      {rows.length === 0 ? (
        <div className="text-sm italic text-muted-foreground border border-dashed border-border rounded-md p-4">{empty}</div>
      ) : rows.map((l) => (
        <LeadCard key={l.id} lead={l} onOpen={setDetail} onGrab={setGrabbing} />
      ))}
    </section>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Grab A Deal</h1>
          <p className="text-sm text-muted-foreground">
            Prospects published by the manager — first to log a contact keeps the lead.
          </p>
        </div>
        {isManager && (
          <Button data-testid="new-lead" onClick={() => { setEditing(null); setFormOpen(true); }}
            className="bg-primary hover:bg-primary/90 text-white">
            <Plus className="w-4 h-4 mr-2" />New Grab A Deal
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, product or remarks…" className="pl-9" />
        </div>
        <select data-testid="lead-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All statuses</option>
          <option value="open">Open for Grabs</option>
          <option value="grabbed">Grabbed</option>
          <option value="deleted">Deleted Deals</option>
        </select>
        {isManager && (
          <select data-testid="lead-grabber-filter" value={grabber} onChange={(e) => setGrabber(e.target.value)}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm">
            <option value="">Anyone</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
      </div>

      <Group title="Open for Grabs" rows={openLeads} testid="group-open"
        empty="Nothing open right now — the manager will publish new leads here." />
      <Group title="Grabbed" rows={grabbed} testid="group-grabbed" empty="No leads grabbed yet." />
      {(deletedLeads.length > 0 || status === "deleted") && (
        <Group title="Deleted Deals" rows={deletedLeads} testid="group-deleted"
          empty="Nothing has been deleted yet." />
      )}

      <LeadForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} onSaved={load} />
      <LeadDetail lead={detail} onClose={() => setDetail(null)} onGrab={setGrabbing} onRefresh={load}
        isManager={isManager} users={users} user={user} onEdit={(l) => { setEditing(l); setFormOpen(true); }} />
      <ContactForm
        open={!!grabbing}
        onClose={() => setGrabbing(null)}
        prefill={grabbing ? {
          company_name: grabbing.company_name,
          website: grabbing.website_links?.[0] || "",
          remarks: grabbing.remarks || "",
        } : null}
        onSave={async (contact) => {
          if (grabbing && contact?.id) await claimLead(grabbing, contact, load);
          setGrabbing(null);
        }}
        fields={fields} products={products} users={users} currentUser={user} />
    </div>
  );
}
